import { Link, useOutletContext } from "react-router";

import { useInfiniteQuery } from "@tanstack/react-query";

import { ArrowRightIcon, BugIcon, CalendarIcon, FolderKanbanIcon, PlusIcon, TicketIcon, UsersIcon } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";

import CreateProjectForm from "@/features/projects/components/CreateProjectForm";
import { PROJECT_ROLE_STYLES, PROJECT_STATUS_STYLES } from "@/features/projects/config";
import { getMyProjects } from "@/features/projects/services/project";
import { EmptyState } from "@/shared/components/EmptyState";
import Error from "@/shared/components/Error";
import { InlineError } from "@/shared/components/InlineError";
import { useErrorModal } from "@/shared/hooks/useErrorModal";
import { PERMISSIONS } from "@/shared/lib/constants/permissions";
import { QUERY_KEYS } from "@/shared/lib/constants/queries";
import { AppError } from "@/shared/lib/utils/errors";

export default function Projects() {
  const { permissions } = useOutletContext() as { permissions: string[] };

  const canReadProjects = permissions.includes(PERMISSIONS.USER_PROJECTS_READ);

  const { data, isPending, error, refetch, fetchNextPage, isFetchingNextPage, hasNextPage } = useInfiniteQuery({
    queryKey: [QUERY_KEYS.GET_MY_PROJECTS],
    queryFn: ({ pageParam }) => getMyProjects(pageParam),
    initialPageParam: null as number | null,
    getNextPageParam: (lastPage) => lastPage.nextStartingAfter,
    enabled: canReadProjects,
  });

  const projects = data?.pages.flatMap((page) => page.projects) ?? [];

  const { open: errorModalOpen, setOpen: setErrorModalOpen, handleRetry } = useErrorModal(error, refetch);

  function handleLoadMore() {
    fetchNextPage();
  }

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="font-heading text-2xl font-semibold">Projects</h1>
          <p className="mt-1 text-sm text-muted-foreground">All projects you're a member of.</p>
        </div>
        <CreateProjectForm permissions={permissions} />
      </div>
      {error && <Error variant="modal" title="Failed to load projects" error={error as AppError | Error} open={errorModalOpen} onOpenChange={setErrorModalOpen} onRetry={handleRetry} />}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
        {!canReadProjects ? (
          <div className="col-span-full">
            <EmptyState icon={BugIcon} title="No access" description="You don't have permission to view projects. Contact an administrator to request access." />
          </div>
        ) : isPending ? (
          Array.from({ length: 6 }).map((_, i) => (
            <Card key={i} size="sm" className="flex flex-col">
              <CardHeader>
                <div className="flex items-start justify-between gap-3">
                  <Skeleton className="size-9 shrink-0" />
                  <div className="flex items-center gap-2">
                    <Skeleton className="h-5 w-16 rounded-full" />
                    <Skeleton className="h-5 w-20 rounded-full" />
                  </div>
                </div>
                <Skeleton className="mt-3 h-4 w-3/4" />
                <Skeleton className="h-3.5 w-full" />
                <Skeleton className="h-3.5 w-2/3" />
              </CardHeader>
              <CardContent className="py-4">
                <ul className="flex flex-col gap-2.5">
                  <li className="flex items-center gap-2">
                    <Skeleton className="size-3.5 shrink-0 rounded" />
                    <Skeleton className="h-3.5 w-24" />
                  </li>
                  <li className="flex items-center gap-2">
                    <Skeleton className="size-3.5 shrink-0 rounded" />
                    <Skeleton className="h-3.5 w-20" />
                  </li>
                  <li className="flex items-center gap-2">
                    <Skeleton className="size-3.5 shrink-0 rounded" />
                    <Skeleton className="h-3.5 w-28" />
                  </li>
                </ul>
              </CardContent>
              <CardFooter className="mt-auto">
                <Skeleton className="h-8 w-full" />
              </CardFooter>
            </Card>
          ))
        ) : error && !errorModalOpen ? (
          <div className="col-span-full">
            <InlineError title="Failed to load projects" description="Something went wrong while fetching your projects." onRetry={handleRetry} />
          </div>
        ) : !error && projects.length === 0 ? (
          <div className="col-span-full">
            <EmptyState
              icon={BugIcon}
              title="No projects yet"
              description="Create your first project to get started tracking bugs and tasks."
              action={
                <CreateProjectForm
                  permissions={permissions}
                  trigger={
                    <Button size="sm">
                      <PlusIcon />
                      New project
                    </Button>
                  }
                />
              }
            />
          </div>
        ) : (
          projects.map((project) => (
            <Card key={project.id} size="sm" className="flex flex-col">
              <CardHeader>
                <div className="flex items-start justify-between gap-3">
                  <div className="flex size-9 shrink-0 items-center justify-center bg-primary/10 text-primary">
                    <FolderKanbanIcon className="size-4" />
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant="secondary" className={PROJECT_STATUS_STYLES[project.status]}>
                      {project.status}
                    </Badge>
                    <Badge variant="secondary" className={PROJECT_ROLE_STYLES[project.role.name]}>
                      {project.role.name}
                    </Badge>
                  </div>
                </div>
                <CardTitle className="mt-3 text-sm">{project.name}</CardTitle>
                <CardDescription className="line-clamp-2">{project.description ?? "No description provided."}</CardDescription>
              </CardHeader>
              <CardContent className="py-4">
                <ul className="flex flex-col gap-2.5">
                  <li className="flex items-center gap-2 text-xs text-muted-foreground">
                    <TicketIcon className="size-3.5 shrink-0" />
                    <span>
                      <span className="font-semibold text-foreground">{project.openTicketCount}</span> open {project.openTicketCount === 1 ? "ticket" : "tickets"}
                    </span>
                  </li>
                  <li className="flex items-center gap-2 text-xs text-muted-foreground">
                    <UsersIcon className="size-3.5 shrink-0" />
                    <span>
                      <span className="font-semibold text-foreground">{project.totalMemberCount}</span> {project.totalMemberCount === 1 ? "member" : "members"}
                    </span>
                  </li>
                  <li className="flex items-center gap-2 text-xs text-muted-foreground">
                    <CalendarIcon className="size-3.5 shrink-0" />
                    <span>Created {project.createdAt.toLocaleDateString("en-US", { month: "short", year: "numeric" })}</span>
                  </li>
                </ul>
              </CardContent>
              <CardFooter className="mt-auto">
                <Button asChild variant="outline" size="sm" className="w-full">
                  <Link to={`/projects/${project.id}`}>
                    View project
                    <ArrowRightIcon />
                  </Link>
                </Button>
              </CardFooter>
            </Card>
          ))
        )}
      </div>
      {!isPending && !error && hasNextPage && (
        <div className="flex items-center justify-center">
          <Button variant="outline" size="sm" onClick={handleLoadMore} disabled={isFetchingNextPage}>
            {isFetchingNextPage ? "Loading..." : "Load more"}
          </Button>
        </div>
      )}
    </div>
  );
}
